import { fetchOverviewData } from './overview';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_KEY || '';

export const getAIResponse = async (userMessage, context) => {
  if (!GEMINI_API_KEY) {
    console.error("DEBUG: Gemini API Key is missing");
    return { success: false, error: 'API Key Missing' };
  }

  const { land, crop, overview, chemicals } = context;

  // التعليمات التفصيلية للمستشار الزراعي الذكي
  const systemPrompt = `
أنت "باشمهندس ديوان"، المستشار الزراعي والاستراتيجي لـ "ديوان الأرض". 

قواعد طول الرد والديناميكية (هام جداً):
1. الرد القصير: إذا كان المستخدم يلقي التحية (أهلاً، سلام)، أو يطلب شيئاً ترفيهياً (قول نكتة)، أو يسأل سؤالاً بسيطاً لا يتطلب تخطيطاً، فاجب باختصار شديد وودود (لا تذكر تفاصيل الأرض أو الحالة المالية إلا إذا سأل عنها).
2. الرد الاستراتيجي الطويل: فقط عندما يطلب الفلاح "خطة" (Plan)، أو يسأل عن "تسميد"، "ري"، أو "مشكلة في الزرع"، هنا يجب أن تكون مفصلاً وتقدم جدولاً زمنياً للأيام (اليوم 1، اليوم 2...) وتشمل الداتا المالية والزراعية.
3. التوازن: لا تفرض نصائح مالية أو زراعية طويلة في كل رسالة؛ كن ذكياً وافهم غرض المستخدم من الرسالة أولاً.

سياق البيانات (الداتا) - استخدمها فقط عند الحاجة للردود الاستراتيجية:
- اسم الأرض: "${land?.name || 'غير محدد'}"
- المحصول: "${crop?.crop_name || 'غير محدد'}" (المساحة: ${crop?.planted_area_feddan || 0} فدان و${crop?.planted_area_qirat || 0} قيراط)
- الحالة المالية: إجمالي المصاريف (${overview?.stats?.totalExpenses || 0})، إجمالي الإيرادات (${overview?.stats?.totalIncome || 0})، الربح صافي (${overview?.stats?.profit || 0}).
- الكيماويات المتوفرة (المخزن): ${chemicals?.map(c => `${c.name} (${c.quantity} ${c.unit})`).join(', ') || 'لا يوجد بيانات مخزن حالياً'}
- آخر 5 عمليات: ${overview?.activities?.slice(0, 5).map(a => `${a.type}: ${a.title}`).join(' | ')}

شخصية الرد: استخدم لغة "فلاحي" قريبة للقلب ("يا هندسة"، "يا غالي").
`;

  const payload = {
    contents: [{ parts: [{ text: `${systemPrompt}\n\nسؤال المستخدم: ${userMessage}` }] }],
    generationConfig: { 
      temperature: 0.7, 
      maxOutputTokens: 2048,
      topP: 0.95
    },
    // تعطيل فلاتر الأمان تماماً لضمان عدم قطع الردود الزراعية أو التقنية بالخطأ
    safetySettings: [
      { category: 'HARM_CATEGORY_HARASSMENT', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_HATE_SPEECH', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT', threshold: 'BLOCK_NONE' },
      { category: 'HARM_CATEGORY_DANGEROUS_CONTENT', threshold: 'BLOCK_NONE' }
    ]
  };

  try {
    // خطوة 1: جلب الموديلات المتاحة لهذا المفتاح (لتجنب الـ 404 بناءً على المنطقة)
    const listRes = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${GEMINI_API_KEY}`);
    const listData = await listRes.json();
    
    if (!listData.models) throw new Error("Could not list Gemini models");

    const availableModels = listData.models.map(m => m.name);
    console.log("DEBUG: All Available Models (Full List):", availableModels.join(', '));

    const findModel = (pattern) => availableModels.find(m => m.toLowerCase().includes(pattern.toLowerCase()));

    // استراتيجية البحث عن الموديلات المستقرة المتاحة في حسابك تحديداً
    const candidateList = [
      findModel('gemini-1.5-flash'),  
      findModel('gemini-flash-latest'), // موجود في قائمتك!
      findModel('gemini-1.5-pro'),    
      findModel('gemini-pro-latest'),  // موجود في قائمتك!
      findModel('gemini-pro'),         
      availableModels[0]               
    ].filter(Boolean);

    // تصفية صارمة: نستبعد كل ما هو 2.0، 2.5، 3.0، و 3.1 حالياً لأنهم للمعاينة (Preview) وكوتا ضيقة جداً
    const excludedKeywords = ['2.0', '2.5', '3.0', '3.1', '3-'];
    let prioritizedModels = candidateList.filter(m => !excludedKeywords.some(key => m.includes(key)));
    
    // إذا القائمة فرغت، نرجع للمتاح مع تجنب الـ 429 قدر الإمكان
    if (prioritizedModels.length === 0) prioritizedModels = candidateList;

    console.log("DEBUG: Final Prioritized Model List:", prioritizedModels);

    for (const modelPath of prioritizedModels) {
      try {
        console.log(`DEBUG: Attemping Gemini Model: ${modelPath}`);
        const response = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/${modelPath}:generateContent?key=${GEMINI_API_KEY}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log(`DEBUG: Result for ${modelPath}:`, result);
          
          const candidate = result.candidates?.[0];
          const aiText = candidate?.content?.parts?.[0]?.text;
          
          if (aiText) {
            console.log("DEBUG: Response Finish Reason:", candidate.finishReason);
            return { success: true, text: aiText.trim() };
          }
        }

        const errJson = await response.json();
        console.warn(`DEBUG: Failed ${modelPath}:`, errJson);
        
        if (response.status === 429) continue; // تجربة الموديل التالي عند القوتا
        throw new Error(`API ${response.status}: ${errJson.error?.message || 'Unknown'}`);

      } catch (innerErr) {
        if (modelPath === prioritizedModels[prioritizedModels.length - 1]) throw innerErr;
      }
    }

    return { success: false, error: 'نأسف يا هندسة، جميع موديلات الذكاء الاصطناعي لا تستجيب حالياً. يرجى المحاولة بعد دقيقة واحدة.' };

  } catch (error) {
    console.error('AI Service Error:', error);
    return { success: false, error: 'حدث خطأ في الخدمة الذكية، يرجى المحاولة بعد دقيقة.' };
  }
};